// Offline storage using IndexedDB for PWA

import { QuestionData } from '../../types/questionData';
import { TestSession } from '../../types/whiteboardProps';

const DB_NAME = 'TahtaOfflineDB';
const DB_VERSION = 1;

// Store names
const STORES = {
  QUESTION_DATA: 'questionData',
  TEST_SESSIONS: 'testSessions',
  SYNC_QUEUE: 'syncQueue',
  CACHED_IMAGES: 'cachedImages',
  USER_PREFERENCES: 'userPreferences'
} as const;

interface SyncQueueItem {
  id: string;
  timestamp: number;
  type: 'questionData' | 'testSession' | 'answer';
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string;
  retryCount: number;
}

interface CachedImage {
  url: string;
  blob: Blob;
  timestamp: number;
  questionId?: string;
}

class OfflineStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    // Check if IndexedDB is available
    if (typeof indexedDB === 'undefined') {
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onerror = () => {
          const error = request.error;
          console.error('IndexedDB open error:', error);
          
          // Handle common IndexedDB errors
          if (error?.name === 'UnknownError') {
            // Don't reject, just resolve without db
            this.db = null;
            resolve();
          } else {
            reject(new Error('Failed to open IndexedDB'));
          }
        };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object stores if they don't exist
        if (!db.objectStoreNames.contains(STORES.QUESTION_DATA)) {
          const questionStore = db.createObjectStore(STORES.QUESTION_DATA, { 
            keyPath: ['testId', 'questionId'] 
          });
          questionStore.createIndex('testId', 'testId', { unique: false });
          questionStore.createIndex('userId', 'userId', { unique: false });
          questionStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORES.TEST_SESSIONS)) {
          const sessionStore = db.createObjectStore(STORES.TEST_SESSIONS, { 
            keyPath: 'testId' 
          });
          sessionStore.createIndex('userId', 'userId', { unique: false });
          sessionStore.createIndex('lastActivity', 'lastActivity', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
          const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { 
            keyPath: 'id',
            autoIncrement: true 
          });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
          syncStore.createIndex('type', 'type', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORES.CACHED_IMAGES)) {
          const imageStore = db.createObjectStore(STORES.CACHED_IMAGES, { 
            keyPath: 'url' 
          });
          imageStore.createIndex('timestamp', 'timestamp', { unique: false });
          imageStore.createIndex('questionId', 'questionId', { unique: false });
        }

        if (!db.objectStoreNames.contains(STORES.USER_PREFERENCES)) {
          db.createObjectStore(STORES.USER_PREFERENCES, { keyPath: 'key' });
        }
      };
      } catch (error) {
        console.error('Error opening IndexedDB:', error);
        reject(error);
      }
    });
  }

  // Question Data Operations
  async saveQuestionData(data: QuestionData): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return;
      }
    }
    
    const transaction = this.db.transaction([STORES.QUESTION_DATA], 'readwrite');
    const store = transaction.objectStore(STORES.QUESTION_DATA);
    
    return new Promise((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save question data'));
    });
  }

  async getQuestionData(testId: string, questionId: string): Promise<QuestionData | null> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return null;
      }
    }
    
    const transaction = this.db.transaction([STORES.QUESTION_DATA], 'readonly');
    const store = transaction.objectStore(STORES.QUESTION_DATA);
    
    return new Promise((resolve, reject) => {
      const request = store.get([testId, questionId]);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(new Error('Failed to get question data'));
    });
  }

  async getAllQuestionData(testId: string): Promise<QuestionData[]> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return [];
      }
    }
    
    const transaction = this.db.transaction([STORES.QUESTION_DATA], 'readonly');
    const store = transaction.objectStore(STORES.QUESTION_DATA);
    const index = store.index('testId');
    
    return new Promise((resolve, reject) => {
      const request = index.getAll(testId);
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get all question data'));
    });
  }

  // Test Session Operations
  async saveTestSession(session: TestSession): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return;
      }
    }
    
    const transaction = this.db.transaction([STORES.TEST_SESSIONS], 'readwrite');
    const store = transaction.objectStore(STORES.TEST_SESSIONS);
    
    return new Promise((resolve, reject) => {
      const request = store.put(session);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save test session'));
    });
  }

  async getTestSession(testId: string): Promise<TestSession | null> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return null;
      }
    }
    
    const transaction = this.db.transaction([STORES.TEST_SESSIONS], 'readonly');
    const store = transaction.objectStore(STORES.TEST_SESSIONS);
    
    return new Promise((resolve, reject) => {
      const request = store.get(testId);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(new Error('Failed to get test session'));
    });
  }

  // Sync Queue Operations
  async addToSyncQueue(item: Omit<SyncQueueItem, 'id'>): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return;
      }
    }
    
    const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.add(item);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to add to sync queue'));
    });
  }

  async getSyncQueue(): Promise<SyncQueueItem[]> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return [];
      }
    }
    
    const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(new Error('Failed to get sync queue'));
    });
  }

  async removeSyncQueueItem(id: string): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return;
      }
    }
    
    const transaction = this.db.transaction([STORES.SYNC_QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.SYNC_QUEUE);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to remove sync queue item'));
    });
  }

  // Image Caching Operations
  async cacheImage(url: string, blob: Blob, questionId?: string): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return;
      }
    }
    
    const transaction = this.db.transaction([STORES.CACHED_IMAGES], 'readwrite');
    const store = transaction.objectStore(STORES.CACHED_IMAGES);
    
    const cachedImage: CachedImage = {
      url,
      blob,
      timestamp: Date.now(),
      questionId
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cachedImage);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to cache image'));
    });
  }

  async getCachedImage(url: string): Promise<Blob | null> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return null;
      }
    }
    
    const transaction = this.db.transaction([STORES.CACHED_IMAGES], 'readonly');
    const store = transaction.objectStore(STORES.CACHED_IMAGES);
    
    return new Promise((resolve, reject) => {
      const request = store.get(url);
      request.onsuccess = () => {
        const result = request.result as CachedImage | undefined;
        resolve(result?.blob || null);
      };
      request.onerror = () => reject(new Error('Failed to get cached image'));
    });
  }

  // User Preferences Operations
  async savePreference(key: string, value: any): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        console.warn('IndexedDB not available, skipping preference save');
        return;
      }
    }
    
    const transaction = this.db.transaction([STORES.USER_PREFERENCES], 'readwrite');
    const store = transaction.objectStore(STORES.USER_PREFERENCES);
    
    return new Promise((resolve, reject) => {
      const request = store.put({ key, value, timestamp: Date.now() });
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to save preference'));
    });
  }

  async getPreference(key: string): Promise<any> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        return null;
      }
    }
    
    const transaction = this.db.transaction([STORES.USER_PREFERENCES], 'readonly');
    const store = transaction.objectStore(STORES.USER_PREFERENCES);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result?.value || null);
      };
      request.onerror = () => reject(new Error('Failed to get preference'));
    });
  }

  // Cleanup Operations
  async cleanupOldData(daysToKeep: number = 30): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        console.warn('IndexedDB not available, skipping cleanup');
        return;
      }
    }
    
    const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);
    
    // Clean up old question data
    const questionTransaction = this.db.transaction([STORES.QUESTION_DATA], 'readwrite');
    const questionStore = questionTransaction.objectStore(STORES.QUESTION_DATA);
    const questionIndex = questionStore.index('updatedAt');
    
    const questionRange = IDBKeyRange.upperBound(cutoffTime);
    const questionRequest = questionIndex.openCursor(questionRange);
    
    questionRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    // Clean up old cached images
    const imageTransaction = this.db.transaction([STORES.CACHED_IMAGES], 'readwrite');
    const imageStore = imageTransaction.objectStore(STORES.CACHED_IMAGES);
    const imageIndex = imageStore.index('timestamp');
    
    const imageRange = IDBKeyRange.upperBound(cutoffTime);
    const imageRequest = imageIndex.openCursor(imageRange);
    
    imageRequest.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest).result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
  }

  // Get storage usage
  async getStorageUsage(): Promise<{ usage: number; quota: number }> {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0
      };
    }
    return { usage: 0, quota: 0 };
  }

  // Clear all data
  async clearAll(): Promise<void> {
    if (!this.db) {
      await this.init();
      if (!this.db) {
        console.warn('IndexedDB not available, skipping clear');
        return;
      }
    }
    
    const storeNames = Object.values(STORES);
    const transaction = this.db.transaction(storeNames, 'readwrite');
    
    const promises = storeNames.map(storeName => {
      return new Promise<void>((resolve, reject) => {
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(new Error(`Failed to clear ${storeName}`));
      });
    });
    
    await Promise.all(promises);
  }
}

// Export singleton instance
export const offlineStorage = new OfflineStorage();

// Export types
export type { SyncQueueItem, CachedImage };